import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Calendar,
  Clock,
  Users,
  CheckCircle,
  ArrowRight,
  Video,
  FileText,
  DollarSign,
  Target,
} from 'lucide-react';
import { CommunityAssessmentData } from '@/types/entities/clients/assessment';
import {
  discoveryCallService,
  DiscoveryCallSlot,
  DiscoveryCallBooking,
  DiscoveryCallAgenda,
} from '@/services/entities/community_subpanel-elect/discoveryCallService';

interface DiscoveryCallSchedulerProps {
  assessmentData: Partial<CommunityAssessmentData>;
  onScheduled: (booking: DiscoveryCallBooking) => void;
  onBack?: () => void;
}

export const DiscoveryCallScheduler: React.FC<DiscoveryCallSchedulerProps> = ({
  assessmentData,
  onScheduled,
  onBack,
}) => {
  const [currentStep, setCurrentStep] = useState<
    'preview' | 'schedule' | 'stakeholders' | 'confirmation'
  >('preview');
  const [availableSlots, setAvailableSlots] = useState<DiscoveryCallSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<DiscoveryCallSlot | null>(null);
  const [agenda, setAgenda] = useState<DiscoveryCallAgenda | null>(null);
  const [additionalStakeholders, setAdditionalStakeholders] = useState<
    Array<{ name: string; email: string; role: string }>
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [booking, setBooking] = useState<DiscoveryCallBooking | null>(null);

  useEffect(() => {
    // Generate personalized agenda
    const personalizedAgenda = discoveryCallService.generatePersonalizedAgenda(assessmentData);
    setAgenda(personalizedAgenda);

    // Load available time slots
    const slots = discoveryCallService.getAvailableSlots();
    setAvailableSlots(slots);
  }, [assessmentData]);

  const handleScheduleCall = async () => {
    if (!selectedSlot) return;

    setIsLoading(true);
    try {
      const newBooking = await discoveryCallService.scheduleDiscoveryCall(
        assessmentData,
        selectedSlot.id,
        additionalStakeholders,
      );
      setBooking(newBooking);
      setCurrentStep('confirmation');
      onScheduled(newBooking);
    } catch (error) {
      console.error('Failed to schedule discovery call:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addStakeholder = () => {
    setAdditionalStakeholders([...additionalStakeholders, { name: '', email: '', role: '' }]);
  };

  const updateStakeholder = (index: number, field: string, value: string) => {
    const updated = additionalStakeholders.map((stakeholder, i) =>
      i === index ? { ...stakeholder, [field]: value } : stakeholder,
    );
    setAdditionalStakeholders(updated);
  };

  const removeStakeholder = (index: number) => {
    setAdditionalStakeholders(additionalStakeholders.filter((_, i) => i !== index));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    const [hour, minute] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hour), parseInt(minute));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getTierBadge = () => {
    const tier = assessmentData.selectedTier || 'silver';
    const colors = {
      silver: 'bg-gray-100 text-gray-800',
      gold: 'bg-blue-100 text-blue-800',
      custom: 'bg-purple-100 text-purple-800',
    };
    return <Badge className={colors[tier]}>{tier.toUpperCase()}</Badge>;
  };

  if (currentStep === 'preview') {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">🎉 Assessment Complete!</h3>
          <p className="text-gray-600">
            Let's schedule your personalized discovery call to discuss your community's
            transformation
          </p>
        </div>

        {/* Assessment Summary */}
        <Card className="bg-linear-to-r from-green-50 to-blue-50 border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Your Community Profile</span>
              {getTierBadge()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Community Details</h4>
                <ul className="space-y-1 text-sm">
                  <li>
                    <strong>Name:</strong> {assessmentData.communityName}
                  </li>
                  <li>
                    <strong>Units:</strong> {assessmentData.totalUnits}
                  </li>
                  <li>
                    <strong>Location:</strong> {assessmentData.location?.city},{' '}
                    {assessmentData.location?.state}
                  </li>
                  {assessmentData.financialPosition?.treasuryAmount && (
                    <li>
                      <strong>Treasury:</strong> $
                      {assessmentData.financialPosition.treasuryAmount.toLocaleString()}
                    </li>
                  )}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Priority Goals</h4>
                <ul className="space-y-1 text-sm">
                  {assessmentData.primaryGoals?.slice(0, 3).map((goal, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Target className="w-3 h-3 text-green-600" />
                      {goal}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personalized Agenda */}
        {agenda && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Your Personalized Agenda ({agenda.duration} minutes)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {agenda.sections.map((section, index) => (
                  <div key={index} className="border-l-4 border-blue-500 pl-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{section.title}</h4>
                      <Badge variant="outline">{section.duration} min</Badge>
                    </div>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {section.topics.map((topic, topicIndex) => (
                        <li key={topicIndex}>• {topic}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* ROI Preview */}
              <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  ROI Projections We'll Discuss
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-green-700">Potential Annual Savings:</span>
                    <div className="font-bold text-green-900">
                      ${agenda.roiProjections.potentialSavings.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <span className="text-green-700">Treasury Growth Potential:</span>
                    <div className="font-bold text-green-900">
                      ${agenda.roiProjections.treasuryGrowth.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between">
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              Back to Assessment
            </Button>
          )}
          <Button
            onClick={() => setCurrentStep('schedule')}
            className="bg-linear-to-r from-blue-600 to-green-600 text-white"
          >
            Schedule Discovery Call
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  if (currentStep === 'schedule') {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Select Your Preferred Time</h3>
          <p className="text-gray-600">
            Choose a convenient time for your personalized {agenda?.duration}-minute discovery call
          </p>
        </div>

        <div className="grid gap-4">
          {availableSlots.map((slot) => (
            <Card
              key={slot.id}
              className={`cursor-pointer transition-all duration-200 ${
                selectedSlot?.id === slot.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'hover:border-gray-300 hover:shadow-md'
              }`}
              onClick={() => setSelectedSlot(slot)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span className="font-medium">{formatDate(slot.date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span>
                        {formatTime(slot.time)} ({slot.timeZone})
                      </span>
                    </div>
                  </div>
                  {selectedSlot?.id === slot.id && (
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Timezone Note */}
        <div className="text-sm text-gray-500 text-center">
          All times shown in Eastern Time (ET). We'll send a calendar invite with your local
          timezone.
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setCurrentStep('preview')}>
            Back
          </Button>
          <Button
            onClick={() => setCurrentStep('stakeholders')}
            disabled={!selectedSlot}
            className="bg-linear-to-r from-blue-600 to-green-600 text-white"
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  if (currentStep === 'stakeholders') {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Who Else Should Join?</h3>
          <p className="text-gray-600">
            Add other stakeholders who should participate in the discovery call
          </p>
        </div>

        {/* Primary Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Primary Contact (You)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="font-medium">{assessmentData.contactName}</div>
              <div className="text-sm text-gray-600">{assessmentData.contactEmail}</div>
              {assessmentData.contactPhone && (
                <div className="text-sm text-gray-600">{assessmentData.contactPhone}</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Additional Stakeholders */}
        <Card>
          <CardHeader>
            <CardTitle>Additional Stakeholders (Optional)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {additionalStakeholders.map((stakeholder, index) => (
              <div key={index} className="grid md:grid-cols-4 gap-3 p-4 border rounded-lg">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={stakeholder.name}
                    onChange={(e) => updateStakeholder(index, 'name', e.target.value)}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={stakeholder.email}
                    onChange={(e) => updateStakeholder(index, 'email', e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select
                    value={stakeholder.role}
                    onValueChange={(value) => updateStakeholder(index, 'role', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="board_member">Board Member</SelectItem>
                      <SelectItem value="president">President</SelectItem>
                      <SelectItem value="treasurer">Treasurer</SelectItem>
                      <SelectItem value="property_manager">Property Manager</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeStakeholder(index)}
                    className="w-full"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}

            <Button variant="outline" onClick={addStakeholder} className="w-full">
              Add Stakeholder
            </Button>
          </CardContent>
        </Card>

        {/* Special Requirements */}
        {assessmentData.specialRequirements && (
          <Card>
            <CardHeader>
              <CardTitle>Special Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">{assessmentData.specialRequirements}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setCurrentStep('schedule')}>
            Back
          </Button>
          <Button
            onClick={handleScheduleCall}
            disabled={isLoading}
            className="bg-linear-to-r from-blue-600 to-green-600 text-white"
          >
            {isLoading ? 'Scheduling...' : 'Confirm Discovery Call'}
          </Button>
        </div>
      </div>
    );
  }

  if (currentStep === 'confirmation') {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Discovery Call Scheduled!</h3>
          <p className="text-gray-600">We've confirmed your personalized consultation</p>
        </div>

        {booking && (
          <Card className="bg-linear-to-r from-green-50 to-blue-50 border-green-200">
            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Call Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span>{formatDate(booking.selectedSlot.date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span>
                        {formatTime(booking.selectedSlot.time)} ({booking.selectedSlot.timeZone})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Video className="w-4 h-4 text-gray-500" />
                      <span>{booking.agenda.duration} minutes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      <span>{1 + booking.stakeholders.length} participant(s)</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">What's Next</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Calendar invite sent to your email
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Personalized agenda prepared
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      ROI analysis ready for review
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Custom proposal in development
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h4 className="font-semibold text-blue-900 mb-3">Before Our Call</h4>
          <ul className="space-y-2 text-sm text-blue-800">
            <li>• Review the personalized agenda we've sent</li>
            <li>• Prepare any specific questions about your community's needs</li>
            <li>• Consider which board members should see the proposal</li>
            <li>• Think about your ideal implementation timeline</li>
          </ul>
        </div>

        <div className="text-center">
          <Button
            onClick={() => (window.location.href = '/pricing/montana')}
            className="bg-linear-to-r from-blue-600 to-green-600 text-white"
          >
            Return to Pricing Page
          </Button>
        </div>
      </div>
    );
  }

  return null;
};
